import React, { useState, useEffect } from "react";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { ProjectsView } from "./ProjectsView";
import { FinanceView } from "./FinanceView";
import { SocialView } from "./SocialView";
import { CalendarView } from "./CalendarView";
import { HomeView } from "./HomeView";
import { SummaryView } from "./SummaryView";
import { FestivalView } from "./FestivalView";
import { EtcView } from "./EtcView";
import { ShootingView } from "./ShootingView";
import { AvansView } from "./AvansView";
import ContactsView from "./ContactsView";
import { InfoView } from "./InfoView";
import { SettingsView, NAV_ITEMS, DEFAULT_USERS } from "./SettingsView";
import { supabase } from "./supabase";

const PASSWORD = "fol";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function LoginScreen({ onLogin, users }) {
  const [val,setVal]=useState(""); const [shake,setShake]=useState(false);
  const attempt=()=>{
    if(val===PASSWORD){localStorage.setItem("fol_auth","full");onLogin("full");return;}
    const u = users.find(x=>x.password && x.password===val);
    if(u){localStorage.setItem("fol_auth",u.id);onLogin(u.id);return;}
    setShake(true);setTimeout(()=>setShake(false),400);setVal("");
  };
  return (
    <div style={{minHeight:"100vh",background:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Inter',sans-serif",padding:"0 24px"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');*{box-sizing:border-box;margin:0;padding:0;}@keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}.shake{animation:shake 0.35s ease;}`}</style>
      <div className={shake?"shake":""} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:20}}>
        <input type="password" value={val} onChange={e=>setVal(e.target.value)} onKeyDown={e=>e.key==="Enter"&&attempt()} autoFocus
          style={{width:180,textAlign:"center",border:"1px solid #e8e4de",borderRadius:6,padding:"10px 14px",fontSize:15,fontFamily:"'Inter',sans-serif",color:"#111",background:"#fff",outline:"none"}}/>
        <button onClick={attempt} aria-label="Giriş yap"
          style={{width:14,height:14,borderRadius:"50%",background:"#111",border:"none",cursor:"pointer",padding:0,flexShrink:0}}/>
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state = { error:null, info:null }; }
  static getDerivedStateFromError(error){ return { error }; }
  componentDidCatch(error, info){ this.setState({ error, info }); console.error("Render error:", error, info); }
  render(){
    if (this.state.error) {
      return (
        <div style={{padding:24,fontFamily:"monospace",fontSize:12,color:"#c0392b",background:"#fff5f5",height:"100%",overflow:"auto",whiteSpace:"pre-wrap"}}>
          <div style={{fontWeight:700,marginBottom:10,fontSize:14}}>Bir hata oluştu — bu ekran görüntüsünü gönder:</div>
          <div style={{marginBottom:10}}>{String(this.state.error?.message || this.state.error)}</div>
          <div style={{fontSize:10,opacity:0.7}}>{this.state.error?.stack}</div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppInner({ accessLevel, users }) {
  const navIds = accessLevel==="full"
    ? [...NAV_ITEMS.map(n=>n.id),"settings"]
    : (users.find(u=>u.id===accessLevel)?.nav || []);
  const NAV = navIds.map(id=>id==="settings"?{id:"settings",label:"Settings"}:NAV_ITEMS.find(n=>n.id===id)).filter(Boolean);

  const [page,setPage]=useState(()=>navIds.includes("home")?"home":(navIds[0]||"home"));
  const [googleToken,setGoogleToken]=useState(localStorage.getItem("google_token")||null);
  const [menuOpen,setMenuOpen]=useState(false);
  const [festivalProjectId,setFestivalProjectId]=useState(null);

  useEffect(()=>{
    if(navIds.length>0 && !navIds.includes(page)) setPage(navIds[0]);
  },[navIds.join(","), page]); // eslint-disable-line

  useEffect(()=>{
    if(accessLevel!=="full" && !users.find(u=>u.id===accessLevel)){
      localStorage.removeItem("fol_auth");
      window.location.reload();
    }
  },[users, accessLevel]);

  function goToFestival(projectId){setFestivalProjectId(projectId);setPage("festivals");}

  const googleLogin=useGoogleLogin({
    onSuccess:t=>{localStorage.setItem("google_token",t.access_token);setGoogleToken(t.access_token);},
    onError:()=>console.error("Google login failed"),
    scope:"https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/calendar",
  });

  const handleLogout=()=>{localStorage.removeItem("fol_auth");localStorage.removeItem("google_token");window.location.reload();};

  return (
    <div style={{height:"100dvh",background:"#fff",fontFamily:"'Inter',sans-serif",color:"#111",display:"flex",overflow:"hidden"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:3px;}::-webkit-scrollbar-thumb{background:#ccc;border-radius:2px;}
        input,textarea,select,button{font-family:'Inter',sans-serif;}
        input:focus,textarea:focus,select:focus{outline:none;}button{cursor:pointer;}
        .side-nav-item{position:relative;font-size:13px;font-weight:400;color:#999;background:none;border:none;padding:7px 0;cursor:pointer;transition:color 0.15s;letter-spacing:0.01em;white-space:nowrap;text-align:left;display:block;width:100%;}
        .side-nav-item:hover{color:#111;}
        .side-nav-item.active{color:#111;font-weight:500;}
        .desktop-sidebar{display:flex;}
        .mobile-menu-btn{display:none;}
        .mobile-nav{display:none;}
        .mobile-header{display:none;}
        @media(max-width:1000px){
          .desktop-sidebar{display:none!important;}
          .mobile-header{display:flex!important;}
          .mobile-nav.open{display:flex!important;}
        }
      `}</style>

      {/* ── Sol dikey menü — masaüstü ── */}
      <nav className="desktop-sidebar" style={{width:168,borderRight:"1px solid #f0ece6",flexDirection:"column",padding:"24px 20px",flexShrink:0,height:"100%",background:"#fff",minWidth:0}}>
        <div style={{fontSize:13,fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",color:"#111",userSelect:"none",marginBottom:28}}>FOL Films</div>
        <div style={{display:"flex",flexDirection:"column",flex:1,overflowY:"auto"}}>
          {NAV.map(n=><button key={n.id} className={`side-nav-item${page===n.id?" active":""}`} onClick={()=>setPage(n.id)}>{n.label}</button>)}
        </div>
        <div style={{paddingTop:16,borderTop:"1px solid #f0ece6",display:"flex",flexDirection:"column",gap:8}}>
          {!googleToken
            ?<button onClick={()=>googleLogin()} style={{background:"none",border:"none",fontFamily:"'JetBrains Mono',monospace",fontSize:9,letterSpacing:"0.08em",textTransform:"uppercase",color:"#999",cursor:"pointer",padding:0,textAlign:"left"}}>Connect Google</button>
            :<span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:9,color:"#2d7a2d",letterSpacing:"0.08em",textTransform:"uppercase"}}>Google ✓</span>}
          <button onClick={handleLogout} style={{background:"none",border:"none",fontFamily:"'JetBrains Mono',monospace",fontSize:9,color:"#bbb",letterSpacing:"0.08em",textTransform:"uppercase",cursor:"pointer",padding:0,textAlign:"left"}}
            onMouseEnter={e=>e.currentTarget.style.color="#111"} onMouseLeave={e=>e.currentTarget.style.color="#bbb"}>Exit</button>
        </div>
      </nav>

      {/* ── Üst bar — mobil ── */}
      <header className="mobile-header" style={{display:"none",height:52,borderBottom:"1px solid #f0ece6",alignItems:"center",padding:"0 16px",background:"#fff",flexShrink:0,position:"sticky",top:0,zIndex:100,width:"100%"}}>
        <div style={{fontSize:13,fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",color:"#111",userSelect:"none",flexShrink:0}}>FOL Films</div>
        <button onClick={()=>setMenuOpen(o=>!o)} style={{marginLeft:"auto",background:"none",border:"none",padding:"4px",display:"flex",flexDirection:"column",gap:4,cursor:"pointer"}}>
          <span style={{display:"block",width:20,height:2,background:"#111",borderRadius:1}}/>
          <span style={{display:"block",width:20,height:2,background:"#111",borderRadius:1}}/>
          <span style={{display:"block",width:20,height:2,background:"#111",borderRadius:1}}/>
        </button>
      </header>

      <nav className={`mobile-nav${menuOpen?" open":""}`} style={{display:"none",flexDirection:"column",background:"#fff",borderBottom:"1px solid #f0ece6",padding:"8px 16px 12px",position:"sticky",top:52,zIndex:99,width:"100%"}}>
        {NAV.map(n=>(
          <button key={n.id} onClick={()=>{setPage(n.id);setMenuOpen(false);}} style={{background:"none",border:"none",textAlign:"left",padding:"10px 0",fontSize:14,fontWeight:page===n.id?600:400,color:page===n.id?"#111":"#888",borderBottom:"1px solid #f0ece6",cursor:"pointer"}}>{n.label}</button>
        ))}
        <div style={{paddingTop:10}}>
          {!googleToken
            ?<button onClick={()=>googleLogin()} style={{background:"none",border:"1px solid #e0dbd4",borderRadius:2,padding:"6px 12px",fontSize:11,fontFamily:"'JetBrains Mono',monospace",color:"#888",cursor:"pointer"}}>Connect Google</button>
            :<span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:"#2d7a2d"}}>Google ✓</span>}
        </div>
      </nav>

      <div style={{display:"flex",flexDirection:"column",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}>
      <ErrorBoundary>
      <div style={{display:"flex",flex:1,minHeight:0,minWidth:0,overflow:"hidden"}}>
        <div style={{display:page==="home"      ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><HomeView googleToken={googleToken} accessLevel={accessLevel}/></div>
        <div style={{display:page==="ortak"     ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><ProjectsView kind="ortak" googleToken={googleToken} onGoFestival={goToFestival}/></div>
        <div style={{display:page==="ana"       ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><ProjectsView kind="ana" googleToken={googleToken} onGoFestival={goToFestival}/></div>
        <div style={{display:page==="etc"       ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><EtcView/></div>
        {accessLevel==="full"&&<div style={{display:page==="finans"    ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><FinanceView googleToken={googleToken}/></div>}
        <div style={{display:page==="sosyal"    ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><SocialView/></div>
        <div style={{display:page==="takvim"    ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><CalendarView googleToken={googleToken}/></div>
        <div style={{display:page==="festivals" ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><FestivalView initialProjectId={festivalProjectId} accessLevel={accessLevel}/></div>
        <div style={{display:page==="summary"   ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><SummaryView googleToken={googleToken} accessLevel={accessLevel}/></div>
        <div style={{display:page==="shooting"  ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><ShootingView/></div>
        <div style={{display:page==="avans"     ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><AvansView accessLevel={accessLevel}/></div>
        <div style={{display:page==="contacts"  ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"auto"}}><ContactsView/></div>
        <div style={{display:page==="info"      ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><InfoView/></div>
        {accessLevel==="full"&&<div style={{display:page==="settings"  ?"flex":"none",flex:1,minWidth:0,minHeight:0,overflow:"hidden"}}><SettingsView/></div>}
      </div>
      </ErrorBoundary>
      </div>
    </div>
  );
}

function isMobileDevice() {
  const uaMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const narrow = window.innerWidth <= 820;
  return uaMobile || narrow;
}

// ── Telefon için tamamen ayrı, sade kabuk ──
// Masaüstündeki 100vh/dvh, sticky, zoom, iç içe flex zincirleri burada YOK.
// Sayfa doğal tarayıcı scroll'una bırakılıyor; olası çökmenin kaynağı
// masaüstü kabuğuysa bu kabuk ondan tamamen bağımsız.
function MobileShell({ accessLevel, users }) {
  const navIds = accessLevel==="full"
    ? [...NAV_ITEMS.map(n=>n.id),"settings"]
    : (users.find(u=>u.id===accessLevel)?.nav || []);
  const NAV = navIds.map(id=>id==="settings"?{id:"settings",label:"Settings"}:NAV_ITEMS.find(n=>n.id===id)).filter(Boolean);

  const [page,setPage]=useState(()=>navIds.includes("home")?"home":(navIds[0]||"home"));
  const [googleToken,setGoogleToken]=useState(localStorage.getItem("google_token")||null);
  const [festivalProjectId,setFestivalProjectId]=useState(null);

  useEffect(()=>{
    if(navIds.length>0 && !navIds.includes(page)) setPage(navIds[0]);
  },[navIds.join(","), page]); // eslint-disable-line

  const googleLogin=useGoogleLogin({
    onSuccess:t=>{localStorage.setItem("google_token",t.access_token);setGoogleToken(t.access_token);},
    onError:()=>console.error("Google login failed"),
    scope:"https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/calendar",
  });

  const handleLogout=()=>{localStorage.removeItem("fol_auth");localStorage.removeItem("google_token");window.location.reload();};

  function goToFestival(projectId){setFestivalProjectId(projectId);setPage("festivals");}

  let content = null;
  if (page==="home") content = <HomeView googleToken={googleToken} accessLevel={accessLevel}/>;
  else if (page==="ortak") content = <ProjectsView kind="ortak" googleToken={googleToken} onGoFestival={goToFestival}/>;
  else if (page==="ana") content = <ProjectsView kind="ana" googleToken={googleToken} onGoFestival={goToFestival}/>;
  else if (page==="etc") content = <EtcView/>;
  else if (page==="finans" && accessLevel==="full") content = <FinanceView googleToken={googleToken}/>;
  else if (page==="sosyal") content = <SocialView/>;
  else if (page==="takvim") content = <CalendarView googleToken={googleToken}/>;
  else if (page==="festivals") content = <FestivalView initialProjectId={festivalProjectId} accessLevel={accessLevel}/>;
  else if (page==="summary") content = <SummaryView googleToken={googleToken} accessLevel={accessLevel}/>;
  else if (page==="shooting") content = <ShootingView/>;
  else if (page==="avans") content = <AvansView accessLevel={accessLevel}/>;
  else if (page==="contacts") content = <ContactsView/>;
  else if (page==="info") content = <InfoView/>;
  else if (page==="settings" && accessLevel==="full") content = <SettingsView/>;

  return (
    <div style={{background:"#fff",fontFamily:"'Inter',sans-serif",color:"#111",minHeight:"100%"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        html,body{margin:0;padding:0;background:#fff;}
        *{box-sizing:border-box;}
        input,textarea,select,button{font-family:'Inter',sans-serif;}
      `}</style>

      <div style={{padding:"14px 16px",borderBottom:"1px solid #f0ece6"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <span style={{fontSize:13,fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase"}}>FOL Films</span>
          <button onClick={handleLogout} style={{background:"none",border:"none",fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:"#bbb",letterSpacing:"0.1em",textTransform:"uppercase",padding:0}}>Exit</button>
        </div>
        <select value={page} onChange={e=>setPage(e.target.value)}
          style={{width:"100%",fontSize:14,padding:"8px 10px",border:"1px solid #ddd",borderRadius:4,background:"#fff",color:"#111"}}>
          {NAV.map(n=><option key={n.id} value={n.id}>{n.label}</option>)}
        </select>
        {!googleToken ? (
          <button onClick={()=>googleLogin()} style={{marginTop:8,width:"100%",background:"none",border:"1px solid #e0dbd4",borderRadius:4,padding:"7px 10px",fontSize:11,fontFamily:"'JetBrains Mono',monospace",color:"#888"}}>Connect Google</button>
        ) : (
          <div style={{marginTop:8,fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:"#2d7a2d"}}>Google ✓</div>
        )}
      </div>

      <div style={{minHeight:400}}>
        {content}
      </div>
    </div>
  );
}

export default function App() {
  const [auth,setAuth]=useState(localStorage.getItem("fol_auth"));
  const [users,setUsers]=useState(DEFAULT_USERS);
  const [mobile] = useState(isMobileDevice());

  useEffect(()=>{
    supabase.from("app_settings").select("value").eq("key","users").maybeSingle().then(({data})=>{
      if(data?.value && data.value.length) setUsers(data.value);
    });
  },[]);

  if(!auth) return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID||""}><LoginScreen onLogin={setAuth} users={users}/></GoogleOAuthProvider>;
  if(mobile) return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID||""}><MobileShell accessLevel={auth} users={users}/></GoogleOAuthProvider>;
  return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID||""}><AppInner accessLevel={auth} users={users}/></GoogleOAuthProvider>;
}
